import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { flow, get, map, omit } from 'lodash';

import { E2E_ATTRIBUTES } from '@shared/types/enums/e2e-attributes.enum';
import { ScrollDirections } from '@shared/types/enums/scroll-directions.enum';

import { ContentScrollComponent } from '@components/scroll/content-scroll';

import { PlatformHelper } from '@shared/helpers/platform-helper/platform-helper';
import { I18nService } from '@app/shared/services/i18n/i18n.service';

import type {
  ColDef,
  GridOptions,
  Column,
  ColumnState,
  GridApi,
  GridReadyEvent,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import type {
  GridScrollModel,
  ScrollBarOffset,
  Sort,
  SortModel,
} from '@components/dm-grid/dm-grid.type';
import type { ScrollStates } from '@components/scroll/content-scroll/content-scroll.types';
import { xviCustomGridComponents } from './dm-grid.common.components';
import { ViewportService } from '@app/shared/services/viewport/viewport.service';

/**
 * col def expand ag col def
 */
export interface DMColDef extends ColDef {
  quickFilterable?: boolean;
  /**
   * Filter component to use for this column.
   * - Set to `true` to use the default filter(will be CustomFilterComponent in the list-grid component).
   * - Set to the name of a provided filter: `number`, `text`, `date`.
   * - Set to a `IFilterComp`.
   */
  filter?: any;
}

/**
 * use this to forbidden grid col quick filter
 * because it will always return empty string for grid quick filter
 * @returns
 */
export const forbiddenQuickFilterFn = () => '';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dm-grid',
  host: {
    '[attr.e2e-attr]': 'e2eAttr',
    '[class.has-header]': 'hasHeader',
    '[class.no-header]': '!hasHeader',
  },
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['dm-grid.custom.style.scss'],
  templateUrl: 'dm-grid.template.html',
  standalone: false,
})
export class DMGridComponent<D> implements OnDestroy, OnInit {
  @Input() public adjustHeightForSingleTouchScroll = false;
  @Input() public infiniteRowsAmount = false;
  @Input() public laneName: string;
  @Input() public isDisplayHorizontalScroll = true;

  /**
   * Should stop row click propagation when custom row click handler is enabled.
   * This is useful when you have a custom row click handler and
   * you don't want the row click event to propagate to the parent component.
   * Default value is true.
   */
  @Input() public shouldStopRowClickPropagation = true;

  @Output() public onFiltersApply: EventEmitter<{ field: string }[]> = new EventEmitter(true);
  @Output() public onHeaderClick: EventEmitter<SortModel[]> = new EventEmitter();
  @Output() public selectionChanged: EventEmitter<SelectionChangedEvent> = new EventEmitter();
  @Output() public viewPortClicked: EventEmitter<Event> = new EventEmitter();
  @Output() public scrollState = new EventEmitter<ScrollStates>();

  @ViewChild(ContentScrollComponent, { read: ElementRef })
  public contentScroll: ElementRef;
  @ViewChild('grid') public grid: NgTemplateOutlet;

  public e2eAttr: string = E2E_ATTRIBUTES.DM_GRID;
  public rowBuffer: number;
  public cacheOverflowSize: number;
  public maxBlocksInCache: number;

  public isTouchDevice: boolean = PlatformHelper.isTouchDevice();

  public controlStates: GridScrollModel = {
    isTopFader: false,
    isBottomFader: false,
    isLeftFader: false,
    isRightFader: false,
  };

  public gridRowsCount: number;
  public components: { [key: string]: any };

  private _dmGridClass = '';
  private _rowData: D[];
  private _columnDefs: ColDef[];
  private _gridOptions: GridOptions;
  private _fullSizeTable: boolean;
  private _defaultGridOptions: GridOptions;
  private _appliedFilters: { field: string; value: string }[] = [];
  private _noRowsTemplate: string;

  private _api: GridApi | null;

  private scrollBarOffset: ScrollBarOffset;

  private readonly SCROLL_EPSILON = 1.0; // Tolerance for floating-point noise in scroll boundaries (px)

  public constructor(
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    private i18nService: I18nService,
    private viewportService: ViewportService,
  ) {
    this._defaultGridOptions = <GridOptions>{
      headerHeight: 60,
      rowHeight: 51,
      animateRows: this.viewportService.isMobileView(),
      onSortChanged: this.onSortChanged.bind(this),
      defaultColDef: <ColDef>{
        resizable: true,
        getQuickFilterText: forbiddenQuickFilterFn,
      },
    };

    this.rowBuffer = 0;
    this.cacheOverflowSize = 2;
    this.maxBlocksInCache = 2;

    const scrollBarWidthWithBorder = 62;

    this.scrollBarOffset = {
      vertical: scrollBarWidthWithBorder + this._defaultGridOptions.rowHeight!,
      horizontal: scrollBarWidthWithBorder,
    };

    this.elementRef.nativeElement.style.setProperty(
      '--grid-header-height',
      `${this._defaultGridOptions.headerHeight}px`,
    );
  }

  public ngOnInit(): void {
    this.components = Object.assign({}, xviCustomGridComponents);
  }

  public onGridReady(event: GridReadyEvent): void {
    this._api = event.api;

    this.updateTableView();

    if (this.fullSizeTable) {
      event.api.sizeColumnsToFit();
    }

    this.setGridScrollParams();

    this.updateGridContentHeightVar();
  }

  public onModelUpdated(event: ModelUpdatedEvent): void {
    if (!this._api) {
      return;
    }

    const isGridRendered =
      this._api['gridBodyCtrl'].getScrollFeature().heightScaler.modelHeight > 1 ||
      this._api['columnModel'].scrollWidth > 1;

    if (!isGridRendered) {
      return;
    }

    this.setGridScrollParams();

    this.updateGridContentHeightVar();
    this.updateTableView();

    if (event.api.getDisplayedRowCount() === 0) {
      event.api.showNoRowsOverlay();
    } else {
      event.api.hideOverlay();
    }
  }

  public onGridSizeChanged() {
    this.updateFaders();
    this.updateGridContentHeightVar();
  }

  public scrollVertically(params: {
    direction: ScrollDirections;
    position?: 'top' | 'bottom' | 'middle' | null;
  }): void {
    if (!this._api) return;

    const lastVisibleRow = this._api['rowRenderer'].lastRenderedRow;
    const firstVisibleRow = this._api['rowRenderer'].firstRenderedRow;
    if (params.direction === ScrollDirections.TOP) {
      this._api.ensureIndexVisible(
        firstVisibleRow !== 0 ? firstVisibleRow - 1 : 0,
        params.position,
      );
    } else if (params.direction === ScrollDirections.BOTTOM) {
      this._api.ensureIndexVisible(lastVisibleRow + 1, params.position);
    } else if (params.direction === ScrollDirections.FULL_BOTTOM) {
      this._api.ensureIndexVisible(this.gridRowsCount - 1, 'bottom');
    } else if (params.direction === ScrollDirections.FULL_TOP) {
      this._api['gridBodyCtrl'].getScrollFeature().scrollToTop();
    }
  }

  public scrollHorizontally(params: { direction: ScrollDirections; step?: number }): void {
    if (!this._api) return;

    if (
      params.direction === ScrollDirections.RIGHT ||
      params.direction === ScrollDirections.FULL_RIGHT
    ) {
      this._api['gridBodyCtrl'].getScrollFeature().scrollHorizontally(params.step);
    } else if (
      params.direction === ScrollDirections.LEFT ||
      params.direction === ScrollDirections.FULL_LEFT
    ) {
      this._api['gridBodyCtrl'].getScrollFeature().scrollHorizontally(-params.step);
    }
  }

  public onSortChanged(event: SortChangedEvent) {
    const toSortModel = (data: ColumnState) => ({
      colId: data.colId,
      sort: <Sort>data.sort,
    });

    const sortModel: SortModel[] = event.api.getColumnState().map(toSortModel);

    this.onHeaderClick.emit(sortModel);
    this.updateTableView(sortModel);
  }

  public onClick(event: Event): void {
    const target: EventTarget | null = event.srcElement || event.target || event.currentTarget;
    const classNames: string | null = (<Element>target).getAttribute('class');

    if (
      this.shouldStopRowClickPropagation &&
      this.isCustomRowClickHandlerEnabled() &&
      this.isGridRowClick(event)
    ) {
      event.stopPropagation();
    }

    if (classNames && classNames.match(/(body-viewport)/gi)) {
      this.viewPortClicked.emit(event);
    }
  }

  public onBodyScroll(): void {
    this.updateFaders();
  }

  @Input()
  public set dmGridClass(value: string) {
    this._dmGridClass = value;
  }

  public get dmGridClass(): string {
    return this._dmGridClass;
  }

  @Input()
  public set rowData(value: D[]) {
    this._rowData = value;
  }

  public get rowData(): D[] {
    return this._rowData;
  }

  @Input()
  public set columnDefs(value: DMColDef[]) {
    this._columnDefs = flow<DMColDef[], DMColDef[], DMColDef[], DMColDef[], DMColDef[]>(
      // map ...args to arr
      (...cols) => cols,
      this.translateHeaderNames.bind(this),
      this.applyCellStyling.bind(this),
      this.applyCellQuickFilter.bind(this),
    )(...value);
  }

  public get columnDefs(): DMColDef[] {
    return this._columnDefs;
  }

  @Input()
  public set fullSizeTable(value: boolean) {
    this._fullSizeTable = value;
  }

  public get fullSizeTable(): boolean {
    return this._fullSizeTable;
  }

  @Input()
  public set gridOptions(options: GridOptions) {
    this._gridOptions = { ...this._defaultGridOptions, ...options };
    this.elementRef.nativeElement.style.setProperty(
      '--grid-header-height',
      `${this._gridOptions.headerHeight}px`,
    );
  }

  public get gridOptions(): GridOptions {
    return this._gridOptions || this._defaultGridOptions;
  }

  @Input()
  public set noRowsTemplate(value: string) {
    this._noRowsTemplate = value;
  }

  public get noRowsTemplate(): string {
    return this._noRowsTemplate;
  }

  public get hasHeader(): boolean {
    return this.gridOptions?.headerHeight !== 0;
  }

  public applyFilters(filtersModel: { field: string; value: string }) {
    const filtersModelIndex = this._appliedFilters.findIndex((f) => f.field === filtersModel.field);

    if (filtersModelIndex !== -1) {
      this._appliedFilters[filtersModelIndex] = filtersModel;
    } else {
      this._appliedFilters.push(filtersModel);
    }

    this.onFiltersApply.emit(this._appliedFilters);
  }

  public updateTableView(updateSortModel?: SortModel[]) {
    // TODO: Delete sorting related functionality, if not used
    const sortModel = updateSortModel || this._api?.getColumnState();
    const e2eAttrRecordContainer = this.elementRef.nativeElement.querySelector(
      '.ag-center-cols-container > div.ag-row.ag-row-no-focus',
    );
    const e2eAttrColumn = this.elementRef.nativeElement.querySelectorAll(
      '.ag-center-cols-container > div.ag-row.ag-row-no-focus > div',
    );

    this._api?.getAllDisplayedColumns().forEach((column: Column) => {
      const isCurrentColumn = sortModel?.length && sortModel[0].colId === column.getId();

      if (!isCurrentColumn) {
        return;
      }

      if (e2eAttrRecordContainer) {
        e2eAttrRecordContainer.setAttribute('e2e-attr', E2E_ATTRIBUTES.ORDER_HISTORY_RECORD);
      }
      e2eAttrColumn.forEach((item: Element) => {
        item.setAttribute('e2e-attr', E2E_ATTRIBUTES.ORDER_HISTORY_RECORD_COLUMN);
      });
    });
    this._api?.refreshCells();
  }

  public onSelectionChanged(event: SelectionChangedEvent) {
    this.selectionChanged.emit(event);
  }

  public ngOnDestroy() {
    this._api = null;
  }

  private translateHeaderNames(colDefs: DMColDef[]): DMColDef[] {
    return colDefs.map((col: DMColDef) => ({
      ...col,
      headerName: this.i18nService.instant(col.headerName),
    }));
  }

  public applyCellQuickFilter(colDefs: DMColDef[]): DMColDef[] {
    return map(colDefs, (col: DMColDef) => ({
      ...omit<ColDef>(col, 'quickFilterable'),
      getQuickFilterText:
        col.getQuickFilterText ?? (col.quickFilterable ? null : forbiddenQuickFilterFn),
    }));
  }

  private applyCellStyling(colDef: DMColDef[]): DMColDef[] {
    return colDef.map((column: DMColDef) => {
      // Apply custom style for sorted cells of the visible columns
      if (column.hide !== true) {
        column.cellClassRules = {
          // The params object has the attributes:
          // value, data, node, colDef, rowIndex, api and context (more on ag-grid Cell Styling)
          'ag-grid-col-sorted': (params: { api: GridApi; colDef: ColDef }) => {
            const sortModel = params.api.getColumnState();
            const columnWithSort = sortModel.find((col) => col.sort !== null);
            return columnWithSort && columnWithSort.colId === params.colDef.field;
          },
        };
      }
      return column;
    });
  }

  private isGridRowClick(event: Event): boolean {
    if (!event) {
      return false;
    }

    return this.getEventPath(event)
      .map((element) => element.className || '')
      .filter((className) => !!className)
      .some((className: string) => className.match(/(ag-row)|(ag-header-row)/gi));
  }

  private isCustomRowClickHandlerEnabled(): boolean {
    const rowClickHandler = get(this._gridOptions, 'onRowClicked', null);

    return !!rowClickHandler && typeof rowClickHandler === 'function';
  }

  private getEventPath(event: Event): Element[] {
    return event['path'] || (event.composedPath && event.composedPath()) || [];
  }

  private setGridScrollParams(): void {
    this.updateFaders();
    this.gridRowsCount = this._api.getDisplayedRowCount();
  }

  private updateGridContentHeightVar(): void {
    if (!this._api) {
      return;
    }

    const headerHeight = this.gridOptions.headerHeight ?? 0;
    const modelHeight = this._api['gridBodyCtrl'].getScrollFeature().heightScaler.modelHeight;
    const pinnedTopHeight: number = this._api['pinnedRowModel']?.getPinnedTopTotalHeight?.() ?? 0;
    const pinnedBottomHeight: number =
      this._api['pinnedRowModel']?.getPinnedBottomTotalHeight?.() ?? 0;
    const totalHeight = headerHeight + modelHeight + pinnedTopHeight + pinnedBottomHeight;

    this.elementRef.nativeElement.style.setProperty('--grid-content-height', `${totalHeight}px`);
  }

  private updateFaders(): void {
    if (!this._api) return;

    const vScrollPosition = this._api['gridBodyCtrl'].getScrollFeature().getVScrollPosition();
    const hScrollPosition = this._api['gridBodyCtrl'].getScrollFeature().getHScrollPosition();
    const bodyWidth = this._api['columnModel'].bodyWidth;
    const modelHeight = this._api['gridBodyCtrl'].getScrollFeature().heightScaler.modelHeight;
    const dmGridElement = <HTMLElement>(<any>this.grid).elementRef.nativeElement.parentElement;

    this.controlStates = this.fullSizeTable
      ? {
          isBottomFader:
            modelHeight - vScrollPosition.bottom > this.SCROLL_EPSILON &&
            dmGridElement.offsetHeight - this.scrollBarOffset.vertical < modelHeight,
          isTopFader: vScrollPosition.top > this.SCROLL_EPSILON,
          isRightFader:
            bodyWidth - hScrollPosition.right > this.SCROLL_EPSILON &&
            dmGridElement.offsetWidth - this.scrollBarOffset.horizontal <= bodyWidth,
          isLeftFader: hScrollPosition.left > this.SCROLL_EPSILON,
        }
      : {
          isBottomFader: modelHeight - vScrollPosition.bottom > this.SCROLL_EPSILON,
          isTopFader: vScrollPosition.top > this.SCROLL_EPSILON,
          isRightFader: bodyWidth - hScrollPosition.right > this.SCROLL_EPSILON,
          isLeftFader: hScrollPosition.left > this.SCROLL_EPSILON,
        };

    this.cd.markForCheck();
  }
}
